<script lang="tsx">
  import { Button, Dialog } from "primevue";
  import { useMediaQuery } from "@vueuse/core";
  import CentreDirectoryEntryForm from "./CentreDirectoryEntryForm.vue";
  import FormSkeleton from "./FormSkeleton.vue";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

  export default {
    components: {
      FormSkeleton, CentreDirectoryEntryForm, Button, Dialog, Icon,
    },
    props: {
      site: {
        type: String,
        required: true,
      },
      entryId: {
        type: String,
        required: true,
      },
      isAuthor: {
        type: Boolean,
        default: false,
      }
    },
    setup() {
      return {
        mediaQueryXsmall: useMediaQuery('(max-width: 640px)'),
      };
    },
    data() { return {
      editVisible: false,
      deleteVisible: false,
      deleting: false,
      directoryPath: /^(.*)\/[^\/]*\/?$/.exec(location.pathname)![1],

      faArrowLeft,
    }},
    computed: {
      isAdmin() {
        const session = this.$session.info;
        return session.value?.groups?.includes("staff") || session.value?.admin === true;
      }
    },
    methods: {
      async deleteCentre() {
        this.deleting = true;
        await this.$session.deleteEntry(parseInt(this.entryId));
        location.pathname = this.directoryPath;
      },
      back(event: PointerEvent) {
        // not a fresh tab, they likely navigated directly from the directory
        if (history.length > 1) {
          history.back();
          event.preventDefault();
        } else {
          // if this is a fresh window/tab, then they still have the directory
          // open elsewhere
          window.close();
          // if this fails, then it was probably a direct link without an opener,
          // so it'll nav to the directory anyways
        }
      }
    }
  };
</script>

<template>
  <div class="buttons">
    <div>
      <Button variant="text" as="a" :href="directoryPath"
        :label="mediaQueryXsmall ? 'Back' : 'Back to directory'" icon-pos="left"
        @click="back">
        <template #icon="slot">
          <Icon :class="[slot.class]" :icon="faArrowLeft"/>
        </template>
      </Button>
    </div>

    <div>
      <Button v-if="isAuthor" @click="editVisible = true;">
        {{ mediaQueryXsmall ? "Edit" : "Edit Centre" }}
      </Button>
      <Button v-if="isAdmin" as="a" :href="`/admin/content/entries/entry/${entryId}?site=${site}`"
        style="text-decoration: none;">
        Edit in Craft
      </Button>
      <Button v-if="isAuthor" @click="deleteVisible = true;" severity="danger">{{ mediaQueryXsmall ? "Delete" : "Delete Centre" }}</Button>
    </div>

    <Dialog v-if="isAuthor" v-model:visible="editVisible"
      modal close-on-escape dismissable-mask closable block-scroll :draggable="false"
      :header="`Editing Centre`">
      <Suspense>
        <CentreDirectoryEntryForm :site="site" :entryId="parseInt(entryId)"
          style="min-width: min(600px, 80vw)"/>
        <template #fallback>
          <FormSkeleton style="min-width: min(600px, 80vw)"/>
        </template>
      </Suspense>
    </Dialog>

    <Dialog v-model:visible="deleteVisible"
      modal close-on-escape dismissable-mask :closable="!deleting" block-scroll :draggable="false"
      :header="`Delete Centre?`" style="max-width: 350px">
      <span>Do you really want to delete this centre directory entry?</span>
      <template #footer>
        <Button label="Cancel"
          :disabled="deleting" @click="deleteVisible = false;"/>
        <Button label="Permanently Delete" severity="danger"
          :loading="deleting" @click="deleteCentre"/>
      </template>
    </Dialog>
  </div>
</template>

<style lang="less" scoped>
  @import (reference) "@/style/main.less";

  .buttons {
    display: flex;
    gap: var(--spacing-inline);
    justify-content: space-between;
    flex-wrap: wrap;

    & > div {
      display: inline-flex;
      gap: var(--spacing-inline);
    }
  }

  .loader {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
